/* global themeVars */

/**
 * Block3d — interactive 3D tabs block with background video switching.
 */
class Block3d {

	/**
	 * @param {Element} block Root block element
	 */
	constructor( block ) {

		this.block = block;
		this.themeUrl = themeVars?.templateUrl ?? '';
		this.activeBgItem = null;
		this.activeTabIndex = -1;

		const inner = block.querySelector( '.b-block-3d__inner' );

		if ( !inner ) {

			return;

		}

		this.inner = inner;
		this.tabs = [...inner.querySelectorAll( ':scope > .b-block-3d__tab' )];

		if ( this.tabs.length === 0 ) {

			return;

		}

		this.debouncedNavigateSlide = this.debounce( ( tab, dir ) => this.navigateSlide( tab, dir ), 300 );
		this.debouncedActivateTab   = this.debounce( ( index ) => this.activateTab( index ), 300 );

		block.classList.add( 'is-initialized' );

		this.bg = document.createElement( 'div' );
		this.bg.className = 'b-block-3d__bg';
		block.prepend( this.bg );

		this.extractBgItems();
		this.buildOverlay();
		this.buildNavigation();

		this.tabClickHandler = ( e ) => this.onTabClick( e );
		this.navClickHandler = ( e ) => this.onNavClick( e );
		this.tabsPanel.addEventListener( 'click', this.tabClickHandler );
		this.overlaySlides.addEventListener( 'click', this.navClickHandler );

		this.videoEnabled = false;
		this.activateTab( 0 );

		if ( 'IntersectionObserver' in window ) {

			this.viewportObserver = new IntersectionObserver(
				( entries ) => {

					if ( entries[ 0 ].isIntersecting ) {

						this.enableVideo();
						this.viewportObserver.unobserve( this.block );
						this.viewportObserver = null;

					}

				},
				{ threshold: 0.1 }
			);
			this.viewportObserver.observe( this.block );

		} else {

			this.enableVideo();

		}

	}

	/**
	 * Extract video media from slides and move them to the background container.
	 */
	extractBgItems() {

		this.bgItems  = new Map();
		this.bgVideos = new Map();

		this.tabs.forEach( ( tab, tabIdx ) => {

			tab.querySelectorAll( '.b-block-3d__slide' ).forEach( ( slide, slideIdx ) => {

				const mediaContainer = slide.querySelector( 'figure.wp-block-video' );

				if ( !mediaContainer ) {

					return;

				}

				const item = document.createElement( 'div' );
				item.className = 'b-block-3d__bg-item';
				item.setAttribute( 'data-tab', tabIdx );
				item.setAttribute( 'data-slide', slideIdx );
				item.innerHTML = mediaContainer.innerHTML;
				this.bg.appendChild( item );
				this.bgItems.set( `${tabIdx}-${slideIdx}`, item );

				mediaContainer.parentNode.removeChild( mediaContainer );

				const vid = item.querySelector( 'video' );

				if ( vid ) {

					vid.muted   = true;
					vid.preload = 'none';

					if ( vid.src ) {

						vid.dataset.src = vid.src;
						vid.removeAttribute( 'src' );

					}

					this.bgVideos.set( item, vid );

				}

			} );

		} );

	}

	/**
	 * Build the overlay structure with tab buttons and content panels.
	 */
	buildOverlay() {

		const heading = this.inner.querySelector( ':scope > .wp-block-heading' );

		const overlay = document.createElement( 'div' );
		overlay.className = 'b-block-3d__overlay';

		this.tabsPanel = document.createElement( 'div' );
		this.tabsPanel.className = 'b-block-3d__tabs-panel wp-block-buttons';
		this.tabsPanel.setAttribute( 'role', 'tablist' );
		this.tabBtns = [];

		this.tabs.forEach( ( tab, index ) => {

			const label = tab.getAttribute( 'data-label' ) || `Tab ${index + 1}`;
			const btnId = `b-block-3d-tab-btn-${index}-${Math.random().toString( 36 ).slice( 2, 7 )}`;

			const btn = document.createElement( 'button' );
			btn.className = 'b-block-3d__tab-btn';
			btn.type = 'button';
			btn.textContent = label;
			btn.setAttribute( 'data-index', index );
			btn.setAttribute( 'role', 'tab' );
			btn.setAttribute( 'id', btnId );
			btn.setAttribute( 'aria-selected', 'false' );
			btn.setAttribute( 'tabindex', index === 0 ? '0' : '-1' );

			tab.setAttribute( 'role', 'tabpanel' );
			tab.setAttribute( 'aria-labelledby', btnId );

			this.tabBtns.push( btn );
			this.tabsPanel.appendChild( btn );

		} );

		this.keydownHandler = ( e ) => this.onTabKeydown( e );
		this.tabsPanel.addEventListener( 'keydown', this.keydownHandler );

		const tabsContent = document.createElement( 'div' );
		tabsContent.className = 'b-block-3d__tabs-content';

		this.tabs.forEach( ( tab ) => tabsContent.appendChild( tab ) );

		const overlaySlides = document.createElement( 'div' );
		overlaySlides.className = 'b-block-3d__overlay-slides';
		overlaySlides.appendChild( tabsContent );

		const overlayHeader = document.createElement( 'div' );
		overlayHeader.className = 'b-block-3d__overlay-header';

		if ( heading ) {

			overlayHeader.appendChild( heading );

		}

		overlayHeader.appendChild( this.tabsPanel );

		overlay.appendChild( overlayHeader );
		overlay.appendChild( overlaySlides );

		this.overlaySlides = overlaySlides;
		this.overlay = overlay;
		this.inner.appendChild( overlay );

	}

	/**
	 * Build prev/next navigation for tabs that have more than one slide.
	 */
	buildNavigation() {

		this.tabSlides  = new Map();
		this.tabNavBtns = new Map();
		this.tabs.forEach( ( tab ) => {

			this.tabSlides.set( tab, [...tab.querySelectorAll( '.b-block-3d__slide' )] );

		} );

		/**
		 * Create an icon img element for the navigation button.
		 *
		 * @param {string} filename Icon filename from the theme img/icons directory.
		 * @return {HTMLImageElement}
		 */
		const buildNavImg = ( filename ) => {

			const img = document.createElement( 'img' );
			img.src    = `${this.themeUrl}/img/icons/${filename}`;
			img.alt    = '';
			img.width  = 24;
			img.height = 24;
			return img;

		};

		/**
		 * Build the prev/next navigation DOM structure used as a cloneable template.
		 *
		 * @return {HTMLDivElement}
		 */
		const buildNavTemplate = () => {

			const prevLink = document.createElement( 'a' );
			prevLink.className = 'b-block-3d__slider-prev';
			prevLink.setAttribute( 'role', 'button' );
			prevLink.setAttribute( 'aria-label', 'Previous' );
			prevLink.setAttribute( 'aria-disabled', 'true' );
			prevLink.setAttribute( 'tabindex', '-1' );
			prevLink.appendChild( buildNavImg( 'icon-arrow-left-1.svg' ) );

			const nextLink = document.createElement( 'a' );
			nextLink.className = 'b-block-3d__slider-next';
			nextLink.setAttribute( 'role', 'button' );
			nextLink.setAttribute( 'aria-label', 'Next' );
			nextLink.setAttribute( 'aria-disabled', 'false' );
			nextLink.setAttribute( 'tabindex', '0' );
			nextLink.appendChild( buildNavImg( 'icon-arrow-right-1.svg' ) );

			const nav = document.createElement( 'div' );
			nav.className = 'b-block-3d__slider-nav';
			nav.appendChild( prevLink );
			nav.appendChild( nextLink );
			return nav;

		};

		const navTemplate = buildNavTemplate();

		this.tabs.forEach( ( tab ) => {

			const slides = this.tabSlides.get( tab );

			if ( slides.length <= 1 ) {

				return;

			}

			const slidesContainer = tab.querySelector( '.b-block-3d__tab-slides' );

			if ( !slidesContainer ) {

				return;

			}

			const nav = navTemplate.cloneNode( true );

			slidesContainer.prepend( nav );

			// children[0] = prev, children[1] = next — no querySelector needed.
			this.tabNavBtns.set( tab, {
				prev: nav.children[ 0 ],
				next: nav.children[ 1 ],
			} );

		} );

	}

	/**
	 * Switch active background item to the one matching tab+slide index.
	 * Old item fades out, new item fades in — no DOM recreation.
	 *
	 * @param {number} tabIdx   Tab index
	 * @param {number} slideIdx Slide index within tab
	 */
	switchBgItem( tabIdx, slideIdx ) {

		const newItem = this.bgItems.get( `${tabIdx}-${slideIdx}` );

		if ( !newItem || newItem === this.activeBgItem ) {

			return;

		}

		if ( this.activeBgItem ) {

			this.activeBgItem.classList.remove( 'is-active' );
			const oldVid = this.bgVideos.get( this.activeBgItem );

			if ( oldVid ) {

				oldVid.pause();

			}

		}

		newItem.classList.add( 'is-active' );
		this.activeBgItem = newItem;

		const newVid = this.bgVideos.get( newItem );

		if ( newVid && this.videoEnabled ) {

			if ( newVid.dataset.src ) {

				newVid.src = newVid.dataset.src;
				newVid.removeAttribute( 'data-src' );

			}

			if ( newVid.src ) {

				newVid.preload     = 'auto';
				newVid.currentTime = 0;
				newVid.play().catch( () => { /* autoplay blocked */ } );

			}

		}

	}

	/**
	 * Update slider prev/next disabled states
	 *
	 * @param {Element} tab Tab element
	 */
	updateSliderNav( tab ) {

		const slides = this.tabSlides.get( tab );
		const current = slides ? slides.find( ( s ) => s.classList.contains( 'is-active' ) ) : null;

		if ( !current || !slides || slides.length <= 1 ) {

			return;

		}

		const navBtns = this.tabNavBtns.get( tab );

		if ( !navBtns ) {

			return;

		}

		const idx = slides.indexOf( current );
		const isPrevDisabled = idx <= 0;
		const isNextDisabled = idx >= slides.length - 1;

		navBtns.prev.classList.toggle( 'is-disabled', isPrevDisabled );
		navBtns.prev.setAttribute( 'aria-disabled', isPrevDisabled ? 'true' : 'false' );
		navBtns.prev.setAttribute( 'tabindex', isPrevDisabled ? '-1' : '0' );

		navBtns.next.classList.toggle( 'is-disabled', isNextDisabled );
		navBtns.next.setAttribute( 'aria-disabled', isNextDisabled ? 'true' : 'false' );
		navBtns.next.setAttribute( 'tabindex', isNextDisabled ? '-1' : '0' );

	}

	/**
	 * Activate a tab by index
	 *
	 * @param {number} index Tab index
	 */
	activateTab( index ) {

		if ( index === this.activeTabIndex ) {

			return;

		}

		this.activeTabIndex = index;

		this.tabBtns.forEach( ( b ) => {

			b.classList.remove( 'is-active' );
			b.setAttribute( 'aria-selected', 'false' );
			b.setAttribute( 'tabindex', '-1' );

		} );
		this.tabs.forEach( ( t ) => t.classList.remove( 'is-active' ) );

		if ( this.tabBtns[index] ) {

			this.tabBtns[index].classList.add( 'is-active' );
			this.tabBtns[index].setAttribute( 'aria-selected', 'true' );
			this.tabBtns[index].setAttribute( 'tabindex', '0' );

		}

		this.tabs[index].classList.add( 'is-active' );

		const slides = this.tabSlides.get( this.tabs[index] );
		if ( slides ) {

			slides.forEach( ( s ) => s.classList.remove( 'is-active' ) );

			if ( slides.length > 0 ) {

				slides[0].classList.add( 'is-active' );

			}

		}

		this.updateSliderNav( this.tabs[index] );
		this.switchBgItem( index, 0 );

	}

	/**
	 * Activate video loading and play the currently active background item.
	 * Called when the block enters the viewport or as a fallback.
	 */
	enableVideo() {

		this.videoEnabled = true;

		if ( this.activeBgItem ) {

			const vid = this.bgVideos.get( this.activeBgItem );

			if ( vid ) {

				if ( vid.dataset.src ) {

					vid.src = vid.dataset.src;
					vid.removeAttribute( 'data-src' );

				}

				if ( vid.src ) {

					vid.preload     = 'auto';
					vid.currentTime = 0;
					vid.play().catch( () => { /* autoplay blocked */ } );

				}

			}

		}

	}

	/**
	 * Handle delegated click on the tab list panel.
	 *
	 * @param {MouseEvent} e
	 */
	onTabClick( e ) {

		const btn = e.target.closest( '.b-block-3d__tab-btn' );

		if ( !btn ) {

			return;

		}

		const index = parseInt( btn.getAttribute( 'data-index' ), 10 );

		if ( !isNaN( index ) ) {

			this.videoEnabled = true;
			this.debouncedActivateTab( index );

		}

	}

	/**
	 * Handle delegated click on the slides overlay (prev/next navigation).
	 *
	 * @param {MouseEvent} e
	 */
	onNavClick( e ) {

		const isPrev = !!e.target.closest( '.b-block-3d__slider-prev' );
		const isNext = !!e.target.closest( '.b-block-3d__slider-next' );

		if ( !isPrev && !isNext ) {

			return;

		}

		const control = e.target.closest( '.b-block-3d__slider-prev, .b-block-3d__slider-next' );

		if ( control?.getAttribute( 'aria-disabled' ) === 'true' ) {

			e.preventDefault();
			return;

		}

		const tab = e.target.closest( '.b-block-3d__tab' );

		if ( !tab ) {

			return;

		}

		this.debouncedNavigateSlide( tab, isPrev ? -1 : 1 );

	}

	/**
	 * Destroy the block instance: clean up videos, remove listeners and created DOM nodes.
	 */
	destroy() {

		this.bg.querySelectorAll( 'video' ).forEach( ( vid ) => {

			vid.pause();
			vid.removeAttribute( 'src' );
			vid.load();

		} );

		if ( this.tabsPanel && this.keydownHandler ) {

			this.tabsPanel.removeEventListener( 'keydown', this.keydownHandler );
			this.keydownHandler = null;

		}

		if ( this.tabsPanel && this.tabClickHandler ) {

			this.tabsPanel.removeEventListener( 'click', this.tabClickHandler );
			this.tabClickHandler = null;

		}

		if ( this.overlaySlides && this.navClickHandler ) {

			this.overlaySlides.removeEventListener( 'click', this.navClickHandler );
			this.navClickHandler = null;

		}

		if ( this.viewportObserver ) {

			this.viewportObserver.unobserve( this.block );
			this.viewportObserver = null;

		}

		if ( this.bg ) {

			this.bg.remove();

		}

		if ( this.overlay ) {

			this.overlay.remove();

		}

		this.activeBgItem   = null;
		this.activeTabIndex = -1;
		this.videoEnabled   = false;
		this.block.classList.remove( 'is-initialized' );

	}

	/**
	 * Handle keyboard navigation within the tab list.
	 * Supports ArrowLeft, ArrowRight, Home, End per WAI-ARIA Tabs pattern.
	 *
	 * @param {KeyboardEvent} e
	 */
	onTabKeydown( e ) {

		const btns = this.tabBtns;
		const currentIdx = btns.indexOf( this.tabsPanel.ownerDocument.activeElement );

		if ( currentIdx === -1 ) {

			return;

		}

		let targetIdx = currentIdx;

		switch ( e.key ) {

		case 'ArrowRight':
			targetIdx = ( currentIdx + 1 ) % btns.length;
			break;

		case 'ArrowLeft':
			targetIdx = ( currentIdx - 1 + btns.length ) % btns.length;
			break;

		case 'Home':
			targetIdx = 0;
			break;

		case 'End':
			targetIdx = btns.length - 1;
			break;

		default:
			return;

		}

		e.preventDefault();
		btns[targetIdx].focus();
		this.activateTab( targetIdx );

	}

	/**
	 * Returns a debounced version of the given function.
	 *
	 * @param {Function} fn    Function to debounce
	 * @param {number}   delay Delay in milliseconds
	 * @return {Function}
	 */
	debounce( fn, delay ) {

		let timer;

		return ( ...args ) => {

			clearTimeout( timer );
			timer = setTimeout( () => fn.apply( this, args ), delay );

		};

	}

	/**
	 * Navigate slides in a tab
	 *
	 * @param {Element} tab       Tab element
	 * @param {number}  direction -1 for prev, 1 for next
	 */
	navigateSlide( tab, direction ) {

		const slides = this.tabSlides.get( tab );
		const current = slides ? slides.find( ( s ) => s.classList.contains( 'is-active' ) ) : null;

		if ( !current || !slides || slides.length <= 1 ) {

			return;

		}

		const arr = slides;
		const idx = arr.indexOf( current );
		const nextIdx = idx + direction;

		if ( nextIdx >= 0 && nextIdx < slides.length ) {

			current.classList.remove( 'is-active' );
			slides[nextIdx].classList.add( 'is-active' );
			this.updateSliderNav( tab );

			const tabIdx = this.tabs.indexOf( tab );
			this.switchBgItem( tabIdx, nextIdx );

		}

	}

}

document.querySelectorAll( '.b-block-3d' ).forEach( ( block ) => new Block3d( block ) );